import {
    hasNonLatinDigits,
    toEnglishDigits,
} from 'utilities/helper/toEnglishNumber'

/**
 * Make every field in the app accept «۱۲۳» exactly as it accepts "123".
 *
 * Folding digits per-field never held: `FormikInput` did it, so login worked,
 * but the amount on «کارت به کارت», the installment counts, the promissory
 * fields and every hand-rolled `<input>` did not — the same person typing the
 * same way got a working screen and a broken one depending on which component
 * the screen happened to use. There are ~50 inputs and more arrive every
 * sprint; the only version of "everywhere" that stays true is one that does
 * not have to be remembered.
 *
 * So: one capture-phase listener on the document. It rewrites the field's
 * value before React's own delegated listener on the root container gets to
 * look at it, which means every `onChange` in the app — Formik's, react-
 * number-format's, react-code-input's, a raw `useState` setter — receives
 * Latin digits and cannot tell the difference.
 */

/**
 * Fields whose value is not text a person typed. Folding digits in them is
 * either meaningless (checkbox, file) or destructive: in a password «۵» and
 * "5" are two different characters and only one of them is the one the
 * account was created with.
 */
const OPAQUE_TYPES = [
    'password',
    'file',
    'checkbox',
    'radio',
    'button',
    'submit',
    'reset',
    'image',
    'color',
    'range',
    'hidden',
]

type TextField = HTMLInputElement | HTMLTextAreaElement

const isTextField = (target: EventTarget | null): target is TextField =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement

/**
 * The prototype's setter, not `element.value = …`.
 *
 * React tracks the last value it saw through an accessor it installs on the
 * node itself; assigning through that accessor updates the tracker too, so
 * React would compare the new value against the new value, conclude nothing
 * changed, and never fire `onChange` — the keystroke would vanish. Writing
 * through the prototype setter updates the DOM and leaves the tracker holding
 * the stale value, which is precisely the "this changed" React looks for.
 */
const nativeValueSetter = (element: TextField) =>
    Object.getOwnPropertyDescriptor(
        element instanceof HTMLTextAreaElement
            ? HTMLTextAreaElement.prototype
            : HTMLInputElement.prototype,
        'value'
    )?.set

/** `selectionStart` throws on input types that have no text selection. */
const caretOf = (element: TextField): [number | null, number | null] => {
    try {
        return [element.selectionStart, element.selectionEnd]
    } catch {
        return [null, null]
    }
}

const foldDigits = (event: Event) => {
    const element = event.target
    if (!isTextField(element)) return
    if (
        element instanceof HTMLInputElement &&
        OPAQUE_TYPES.indexOf(element.type) !== -1
    )
        return
    // Escape hatch for a field that genuinely wants what was typed.
    if (element.dataset.faDigits === 'keep') return

    const typed = element.value
    if (!hasNonLatinDigits(typed)) return

    // One character in, one character out, so the caret belongs at the same
    // offset afterwards — but writing `value` moves it to the end of the
    // field, so it has to be put back by hand.
    const [start, end] = caretOf(element)

    const setValue = nativeValueSetter(element)
    if (setValue) setValue.call(element, toEnglishDigits(typed))
    else element.value = toEnglishDigits(typed)

    if (start !== null) {
        try {
            element.setSelectionRange(start, end)
        } catch {
            /* no selection on this input type; the caret is already at the end */
        }
    }
}

/** Returns the uninstall function, for tests. */
export function installPersianDigitInput(root: Document = document) {
    root.addEventListener('input', foldDigits, true)
    return () => root.removeEventListener('input', foldDigits, true)
}
